/* GRIZZLY — PDP subscribe / one-time selector.  Replaces Payeo's widget.
 * ---------------------------------------------------------------------------
 * Payeo's `subscription_widget` block used to render the two options on bundle
 * PDPs. Its app embed hijacked the checkout button and built a draft order, so
 * a native Shopify `selling_plan` never reached checkout. With the embed off it
 * does, and then:
 *
 *   - the cart becomes a subscription CONTRACT, which the PayPlus gateway
 *     cannot pay — checkout shows no payment method at all; and
 *   - the plan's 15% lands on top of a variant that is ALREADY priced at the
 *     subscriber price, so ₪243.95 became ₪207.36 (captured live 2026-08-07).
 *
 * So this module never emits a selling_plan. Subscribing means two things and
 * only two things:
 *
 *   1. swap the cart `id` to the `*-subscription` variant — priced at the
 *      subscriber price AND sitting in the "Bundles + Gifts" delivery profile,
 *      which is Free with no minimum (the regular packs are in General: ₪35
 *      under ₪300, and a 4-box at ₪165.75 would be charged); and
 *   2. carry the `_GRZ_Sub_*` line-item properties, which is what PayPlus
 *      and our own adoption (`subs/src/core/payeo-properties.ts`) read.
 *
 * The bundle properties (`ריחות`, `_bb-id`, `_bb-name`, `_bb-variants`) are
 * written by the scent picker in `templates/product.bundles.json`, not here.
 * `_bb-variants` is what adoption derives the box size from, so it has to
 * survive — see the note on intercept ordering below.
 *
 * Config comes from the `grz_subscribe_options` custom_liquid block.
 */
(function () {
  'use strict';

  var CFG = window.GRZ_PDP_SUB;
  if (!CFG || !CFG.sub || !CFG.sub.id || !CFG.oneTime || !CFG.oneTime.id) return;
  if (window.__grzPdpSubInit) return;
  window.__grzPdpSubInit = true;

  var PLAN_NAME = CFG.planName || 'תדירות מנוי: 3 חודשים';
  var CURRENCY = '₪';
  var SUB_PROP_KEYS = [
    '_GRZ_Sub_Plan_ID',
    '🔁 מנוי מתחדש',
    '_GRZ_Sub_Plan_Name',
    '_GRZ_Sub_Price_Per_Delivery',
    '_GRZ_Sub_Compare_At_Price',
    '_GRZ_Sub_Discount_Type',
    '_GRZ_Sub_Discount_Value',
    '_GRZ_Sub_Currency'
  ];

  /* Subscribing is the default, matching what Payeo's widget did (the
     `?selling_plan` param in layout/theme.liquid pre-selected it at render and
     a poll clicked the radio). Changing that default is a pricing decision, not
     a refactor, so it is preserved exactly. */
  var state = { active: CFG.defaultSub !== false };
  CFG.state = state;

  function agorot(v) { return Math.round(Number(v) || 0); }
  function ils(a) { return (agorot(a) / 100).toFixed(2); }
  function money(a) { return ils(a) + CURRENCY; }
  /* 2026-10-21 ShoppingIL box: an optional `CFG.anchor` (agorot) = the box's worth at real store prices, gifts
     included. When a page sets it, BOTH options strike it (and the badge counts from it). Unset = unchanged. */
  var ANCHOR = agorot(CFG.anchor || 0);
  /* 2026-11-02 ShoppingIL 6-pack: the subscription's anchor also counts the free shipping (one-time pays it), and the percent
     rounds down so the page's price block says the hero's 52% (founder: "include the shipping ... make it match"). */
  var ANCHOR_SUB = agorot(CFG.anchorSub || 0) || ANCHOR;

  var ONE = agorot(CFG.oneTime.price);
  var SUB = agorot(CFG.sub.price);
  /* 2026-10-24 sitewide sale (snippets/grz-sale.liquid): a plain subscription box (*-pack-subscription) gets 20%
     below list on the FIRST box - a Shopify automatic discount of 1/17 on top of the 15% - then 15% on every box
     after. Only the variants listed in the config; single bars and the "+ מתנות" boxes are not in the sale.
     SUB_FIRST is exactly what the cart charges (agorot, same rounding). SUB stays the recurring price elsewhere. */
  var SALE = (window.GRZ_SALE && window.GRZ_SALE.active && window.GRZ_SALE.active() &&
              window.GRZ_SALE.sub_boxes && window.GRZ_SALE.sub_boxes[String(CFG.sub.id)]) ? window.GRZ_SALE : null;
  var SUB_FIRST = SALE ? SALE.boxFirst(SUB) : SUB;   /* = the cart's number to the agora (Shopify rounds down) */
  /* one-time at the 10% price (founder 2026-10-24 "show it everywhere, also in pdps"); not on the launch box */
  var ONE_SHOW = (window.GRZ_SALE && window.GRZ_SALE.has(CFG.handle)) ? window.GRZ_SALE.one(ONE) : ONE;
  var DISCOUNT_PCT = ONE > 0 ? Math.round((ONE - SUB) / ONE * 100) : 0;

  /* ── the form ─────────────────────────────────────────────────────────────
     Scoped hard. A bundle PDP also renders cart-drawer upsell forms and
     recommended-product cards, and leaking a variant swap onto one of those
     would silently sell the wrong thing. */
  function mainForm() {
    if (CFG.formId) {
      var byId = document.getElementById(CFG.formId);
      if (byId) return byId;
    }
    var forms = document.querySelectorAll('form[action*="/cart/add"]:not(.installment)');
    for (var i = 0; i < forms.length; i++) {
      if (isMainForm(forms[i])) return forms[i];
    }
    return null;
  }

  function isMainForm(formEl) {
    if (!formEl || !formEl.action || formEl.action.indexOf('/cart/add') === -1) return false;
    if (formEl.classList && formEl.classList.contains('installment')) return false;
    if (CFG.formId && formEl.id === CFG.formId) return true;
    if (formEl.closest('cart-drawer, cart-drawer-items, .cart-drawer, [data-cart-drawer]')) return false;
    var mount = document.getElementById('grzPdpSub');
    if (!mount) return false;
    var formSection = formEl.closest('.shopify-section');
    var mountSection = mount.closest('.shopify-section');
    if (formSection && mountSection && formSection !== mountSection) return false;
    return true;
  }

  function subProps() {
    return {
      /* Fallback literal, same as cart-subscribe-upgrade and the collection
         cards. An empty plan id ships a line the subscription engine cannot
         adopt - the customer pays the subscriber price and no subscription
         exists (15 of them, 13-24/08). Config may lie; this must not. */
      '_GRZ_Sub_Plan_ID': String(CFG.planId || '4691067103'),
      '🔁 מנוי מתחדש': 'כל 3 חודשים',
      '_GRZ_Sub_Plan_Name': PLAN_NAME,
      '_GRZ_Sub_Price_Per_Delivery': ils(SUB),
      '_GRZ_Sub_Compare_At_Price': ils(ONE),
      '_GRZ_Sub_Discount_Type': 'percentage',
      '_GRZ_Sub_Discount_Value': String(DISCOUNT_PCT),
      '_GRZ_Sub_Currency': CURRENCY
    };
  }

  /* Real hidden inputs as well as the FormData layer below. Belt and braces:
     if the theme ever serialises the form by walking `form.elements` instead of
     `new FormData(form)`, the properties still travel. The `id` swap cannot be
     done this way without fighting the theme's own variant input, so that one
     lives in the FormData layer alone — which is the same mechanism the scent
     picker has used in production for months. */
  function syncHiddenInputs() {
    var form = mainForm();
    if (!form) return;
    var props = subProps();
    SUB_PROP_KEYS.forEach(function (key) {
      var name = 'properties[' + key + ']';
      var input = form.querySelector('input[name="' + cssEscape(name) + '"]');
      if (!state.active) {
        if (input && input.dataset.grzPdpSub === '1') input.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.grzPdpSub = '1';
        form.appendChild(input);
      }
      input.value = props[key];
    });
    /* Never leave a native plan sitting in the form. */
    form.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]')
      .forEach(function (el) { el.value = ''; });
  }

  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return s.replace(/([\[\]"'\\])/g, '\\$1');
  }

  /* ── FormData intercept — the authoritative layer ─────────────────────────
     Chains safely with the scent picker's own FormData patch in either install
     order: whichever runs inner sets its fields first and the outer one adds
     its own without clearing them. The picker only swaps `id` when a
     selling_plan is present, and we always delete that, so it never fights this
     swap. */
  (function () {
    var Orig = window.FormData;

    function Patched(formEl) {
      var fd = new Orig(formEl);
      if (!isMainForm(formEl)) return fd;

      fd.delete('selling_plan');

      if (state.active) {
        fd.set('id', String(CFG.sub.id));
        /* Adoption refuses a subscription line with quantity ≠ 1 (it cannot
           tell two boxes from one box billed twice), so a subscribe add is
           always a single box. The quantity control is hidden while subscribe
           is selected, so this can only fire on a stale value. */
        fd.set('quantity', '1');
        var props = subProps();
        Object.keys(props).forEach(function (k) {
          fd.set('properties[' + k + ']', props[k]);
        });
      } else {
        fd.set('id', String(CFG.oneTime.id));
        SUB_PROP_KEYS.forEach(function (k) { fd.delete('properties[' + k + ']'); });
      }
      return fd;
    }

    Patched.prototype = Orig.prototype;
    window.FormData = Patched;
  })();

  /* ── price display ────────────────────────────────────────────────────────
     Scoped to this section's price block and the sticky ATC. The Payeo-era
     version queried the whole document, which also hit cart-drawer prices. */
  function priceRoots() {
    var roots = [];
    if (CFG.priceId) {
      var el = document.getElementById(CFG.priceId);
      if (el) roots.push(el);
    }
    document.querySelectorAll('sticky-atc, .sticky-atc, .sticky-atc-container')
      .forEach(function (el) { roots.push(el); });
    return roots;
  }

  function paintPrice() {
    var now = state.active ? SUB_FIRST : ONE_SHOW;
    var anchorNow = state.active ? ANCHOR_SUB : ANCHOR;
    var was = anchorNow > 0 ? anchorNow : ((state.active || ONE_SHOW < ONE) ? ONE : agorot(CFG.oneTime.compareAt || 0));
    var showCompare = was > now;

    priceRoots().forEach(function (root) {
      root.querySelectorAll('.price-item--sale.price-item--last').forEach(function (el) {
        if (!el.closest('.price__compare-price')) el.textContent = money(now);
      });
      root.querySelectorAll('.price__regular .price-item--regular').forEach(function (el) {
        el.textContent = money(now);
      });
      root.querySelectorAll('.sticky-atc__price, .sticky-atc__price-block .price-item')
        .forEach(function (el) {
          if (el.children.length === 0) el.textContent = money(now);
        });
      root.querySelectorAll('.price__compare-price').forEach(function (el) {
        var inner = el.querySelector('.price-item--regular') || el.querySelector('s');
        if (showCompare && inner) {
          inner.textContent = money(was);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      root.querySelectorAll('.sticky-atc__compare-at').forEach(function (el) {
        if (showCompare) { el.textContent = money(was); el.style.display = ''; }
        else { el.style.display = 'none'; }
      });
      root.querySelectorAll('.price').forEach(function (el) {
        el.classList.toggle('price--on-sale', showCompare);
      });
      root.querySelectorAll('.price__badge-sale .nowrap, .sticky-atc__sale-badge').forEach(function (el) {
        var pct = showCompare ? (CFG.anchorSub ? Math.floor : Math.round)((was - now) / was * 100) : 0;
        el.textContent = pct > 0 ? ('חסכו ' + pct + '%') : '';
        var badge = el.closest('.price__badge-sale, .sticky-atc__sale-badge');
        if (badge) badge.style.display = pct > 0 ? '' : 'none';
      });
    });
  }

  /* Quantity is meaningless for a subscription line — see the FormData note. */
  function paintQuantity() {
    var form = mainForm();
    if (!form) return;
    var input = form.querySelector('input[name="quantity"]');
    if (!input) return;
    if (state.active) input.value = '1';
    var wrap = input.closest('.product-form__quantity') || input.closest('.quantity');
    if (wrap) wrap.style.display = state.active ? 'none' : '';
  }

  /* ── markup ───────────────────────────────────────────────────────────────
     Structure and copy are lifted from the bundle-builder's mode selector
     (`.gbb-mode-*` in sections/grizzly-bundle-builder.liquid) — the same
     control the S&S, first-order and listicle pages have always used. The PDP
     should not read as a second, unrelated subscribe UI.

     One deliberate difference, because a bundle PDP subscribes against the
     *-subscription variant rather than the *-gifts one: no gift grid and no
     "מתנות" claim. The gift ladder belongs to the *-gifts family; promising
     gifts here would be a lie on the packing slip.

     Also unlike the builder, the one-time card is the plain option: title and price, no "what you give
     up" crosses (founder, 2026-09-11). The case for subscribing is made by the
     subscription card's own benefits, shown at full strength either way. */
  function render(mount) {
    /* Above ₪100 a shekel figure reads bigger than a percentage (the reverse
       holds below ₪100, which is why the single bar keeps its 15%). Whole
       shekels, rounded DOWN - a saving is never overstated. */
    /* PRICE saving only - never fold the one-time ₪35 shipping in. The 4-pack
       already shows free shipping as its own line, and counting it here made the
       4-pack read ₪64 vs the 6-pack's ₪43: the smaller pack looked like the better
       deal, against the always-the-6-pack rule. Savings must rise with pack size
       (29 -> 43 -> 68). Founder, 2026-09-11. */
    var saving = Math.floor((ONE - SUB) / 100);

    mount.innerHTML =
      '<div class="grz-sub-opts" role="radiogroup" aria-label="סוג רכישה">' +

        /* HYBRID (founder + three advisors, 2026-09-11): subscription card on TOP
           with the banner; one-time stays PRE-SELECTED below as a clean title +
           price card. The subscription's case is made with true gains only -
           never red ✗s on the default (brain/decisions/2026-09-06-pdp-default-one-time.md). */
        '<label class="grz-sub-opt' + (state.active ? ' is-selected' : '') + '" data-grz-opt="sub">' +
          '<input type="radio" name="grz-purchase-mode" value="sub"' + (state.active ? ' checked' : '') + '>' +
          '<span class="grz-sub-opt__flag">הכי משתלם</span>' +
          '<span class="grz-sub-opt__head">' +
            '<span class="grz-sub-opt__radio" aria-hidden="true"></span>' +
            '<span class="grz-sub-opt__titles">' +
              '<span class="grz-sub-opt__title">מנוי - תירשמו ותחסכו</span>' +
              '<span class="grz-sub-opt__freq">משלוח כל ' + (CFG.months || 3) + ' חודשים</span>' +
            '</span>' +
            '<span class="grz-sub-opt__prices">' +
              '<span class="grz-sub-opt__compare">' + money(ANCHOR_SUB > 0 ? ANCHOR_SUB : ONE) + '</span>' +
              '<span class="grz-sub-opt__price">' + money(SUB_FIRST) + '</span>' +
            '</span>' +
          '</span>' +
          '<span class="grz-sub-opt__body">' +
            /* Two headings, one visible at a time, CSS-swapped by selection —
               the same move the builder makes with its gift grid. The benefits
               stay on screen when one-time is picked, at full strength; a list
               that vanishes takes the reason to come back with it. */
            '<span class="grz-sub-opt__benefits-title">' +
              '<span data-bt="sub">הטבות מנוי רבעוני</span>' +
              '<span data-bt="one">מה מפספסים בלי מנוי</span>' +
            '</span>' +
            /* Numbers sit mid-sentence on purpose: leading with one renders it on
               the wrong side under RTL bidi (seen on the DEV preview). */
            /* sale (founder 2026-10-24, approved copy + /hormozi): stack the two gains - the 20% hook for today,
               the 15% as a permanent benefit - in the card's own checkmark list; no extra element, never "רק" */
            (SALE ? benefit('<strong>' + SALE.sub_first_pct + '% על המארז הראשון</strong>') +
                    benefit(SALE.sub_pct + '% על כל מארז אחריו')
                  : benefit(saving > 0 ? 'חוסכים ' + saving + '₪ בכל משלוח'
                                       : 'הנחה קבועה של ' + DISCOUNT_PCT + '% בכל משלוח')) +
            /* Free shipping on every pack, including the 6/10-pack whose one-time
               order also ships free from ₪300 (₪250 between 2026-09-11 and 09-23), in place
               of the reserved-stock line. "עד הבית" is the checkout rate's own name
               ("משלוח חינם עד הבית", Katz courier), and line 1 already says "בכל משלוח". */
            benefit('משלוח חינם עד הבית') +
            benefit('אפשר לדלג, לשנות או לבטל בכל רגע') +
          '</span>' +
        '</label>' +
        '<label class="grz-sub-opt' + (state.active ? '' : ' is-selected') + '" data-grz-opt="one">' +
          '<input type="radio" name="grz-purchase-mode" value="one"' + (state.active ? '' : ' checked') + '>' +
          '<span class="grz-sub-opt__head">' +
            '<span class="grz-sub-opt__radio" aria-hidden="true"></span>' +
            '<span class="grz-sub-opt__titles">' +
              '<span class="grz-sub-opt__title">רכישה חד פעמית</span>' +
              '<span class="grz-sub-opt__freq">משלוח אחד בלבד</span>' +
            '</span>' +
            '<span class="grz-sub-opt__prices">' +
              (ONE_SHOW < ONE
                ? '<span class="grz-sub-opt__compare">' + money(ONE) + '</span>'
                : (ANCHOR > 0 ? ANCHOR : agorot(CFG.oneTime.compareAt)) > ONE
                  ? '<span class="grz-sub-opt__compare">' + money(ANCHOR > 0 ? ANCHOR : CFG.oneTime.compareAt) + '</span>'
                  : '') +
              '<span class="grz-sub-opt__price">' + money(ONE_SHOW) + '</span>' +
            '</span>' +
          '</span>' +
        '</label>' +
      '</div>';
  }

  function benefit(text) {
    return '<span class="grz-sub-opt__benefit">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="20 6 9 17 4 12"/></svg><span>' + text + '</span></span>';
  }


  function select(mode, mount) {
    var next = mode === 'sub';
    state.active = next;
    mount.querySelectorAll('.grz-sub-opt').forEach(function (el) {
      var on = el.getAttribute('data-grz-opt') === mode;
      el.classList.toggle('is-selected', on);
      var radio = el.querySelector('input[type="radio"]');
      if (radio) radio.checked = on;
    });
    syncHiddenInputs();
    paintPrice();
    paintQuantity();
    document.dispatchEvent(new CustomEvent('grz:purchase-mode', { detail: { subscription: next } }));
  }

  function boot() {
    var mount = document.getElementById('grzPdpSub');
    if (!mount) return;

    /* ?selling_plan= no longer pre-selects the subscription (2026-09-11). Old ad
       and card links still carry it, and honouring it quietly overrode the
       one-time default decided on 2026-09-06 - the unaware-subscriber path.
       Only an explicit ?purchase=one is read; the default stays one-time. */
    try {
      var qp = new URLSearchParams(window.location.search);
      if (qp.get('purchase') === 'one') state.active = false;
    } catch (e) { /* older browsers: keep the default */ }

    render(mount);
    mount.addEventListener('change', function (e) {
      var radio = e.target.closest('input[name="grz-purchase-mode"]');
      if (!radio) return;
      select(radio.value, mount);
    });
    /* Whole card clickable. Idempotent with the `change` handler above — a
       click on the radio itself fires both, and `select` is a pure re-render. */
    mount.addEventListener('click', function (e) {
      var label = e.target.closest('.grz-sub-opt');
      if (!label) return;
      select(label.getAttribute('data-grz-opt'), mount);
    });

    select(state.active ? 'sub' : 'one', mount);

    /* The theme re-renders the price block and the sticky ATC after load
       (section rendering, sticky-ATC clone). Repaint when they change, but stop
       once things settle so this is not a permanent observer on <body>. */
    var repaints = 0;
    var obs = new MutationObserver(function () {
      if (++repaints > 40) { obs.disconnect(); return; }
      paintPrice();
      paintQuantity();
    });
    var priceEl = CFG.priceId ? document.getElementById(CFG.priceId) : null;
    if (priceEl) obs.observe(priceEl, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
